import type { TimelineEvent } from "../queries/useCustomer360";
import type { ConversationDisposition } from "../queries/useQuote360";

// Mirror of the server contract in dashboard-backend-relance/routes/quotes.js:
// HANDLED, CLOSED and CONVERTED (and their legacy lowercase spellings) stop
// the follow-up cadence, so they require a reason from the server enum. A
// named reason is sufficient; only Other requires a free-text comment of at
// least MIN_MARK_COMMENT_CHARS after trimming.
// KEEP_FOLLOWUP keeps the cadence alive and stays one-click. REMINDER_SET is
// rejected by the server on the mark route (unsupported_disposition_state):
// reminders are created through the dedicated reminder endpoints, never by a
// disposition mark. This UI never sends REMINDER_SET on the mark route.

export const MIN_MARK_COMMENT_CHARS = 15;

export const MARK_REASON_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "spoke_on_phone", label: "Spoke with the customer on the phone" },
  { value: "sms_sent_private_phone", label: "Sent SMS from private phone" },
  { value: "customer_asked_stop", label: "Customer asked us to stop" },
  { value: "duplicate_quote", label: "Duplicate quote" },
  { value: "wrong_number", label: "Wrong number / bad contact info" },
  { value: "price_final_no", label: "Final no on price" },
  { value: "went_with_competitor", label: "Went with a competitor" },
  { value: "job_won", label: "Job won / converted" },
  { value: "other", label: "Other (explain in the comment)" },
];

const REASON_VALUES = new Set(MARK_REASON_OPTIONS.map((option) => option.value));

const JUSTIFIED_DISPOSITIONS = new Set<ConversationDisposition>([
  "HANDLED",
  "CLOSED",
  "CONVERTED",
  "handled",
  "client_called",
  "call_received",
  "closed",
  "antonio_sms_sent",
  "antonio_email_sent",
]);

export function requiresJustification(disposition: ConversationDisposition): boolean {
  return JUSTIFIED_DISPOSITIONS.has(disposition);
}

export function requiresMarkComment(reasonCode: string): boolean {
  return reasonCode === "other";
}

export function isMarkJustificationValid(reasonCode: string, comment: string): boolean {
  if (!REASON_VALUES.has(reasonCode)) return false;
  return !requiresMarkComment(reasonCode)
    || comment.trim().length >= MIN_MARK_COMMENT_CHARS;
}

// The subset of QueryClient the pre-mark refresh needs. Narrow on purpose so
// tests can drive it without React Query plumbing.
export interface MarkRefetchClient {
  refetchQueries(filters: { queryKey: unknown[] }): Promise<unknown>;
  getQueryState(queryKey: unknown[]): { status: string } | undefined;
}

// PR #755 review debt: the Quote360 refetch fired before the justification
// dialog used to be fire-and-forget (invalidateQueries with the promise
// dropped), so a fast operator could paste a valid comment and confirm a
// Handled/Closed mark while a newer inbound message was still in flight.
// The dialog is now allowed to open only after this refetch has settled
// successfully; an error or unknown state keeps it closed so the operator
// never justifies a decision against a timeline that failed to refresh.
export async function refreshTimelineBeforeMark(
  client: MarkRefetchClient,
  quoteId: string,
): Promise<boolean> {
  const queryKey = ["quote-360", quoteId];
  await client.refetchQueries({ queryKey });
  return client.getQueryState(queryKey)?.status === "success";
}

export interface LatestInboundEvent {
  event: TimelineEvent;
  displayText: string;
}

// The confirmation dialog shows the customer's LAST inbound message before a
// Handled/Closed click can be confirmed. Prefer the excerpt: email
// body_plain drags the quoted thread on almost the whole corpus.
export function latestInboundEvent(timeline: TimelineEvent[]): LatestInboundEvent | null {
  const inbound = timeline
    .filter((event) => event.type === "reply_received" && event.payload?.direction === "inbound")
    .sort((a, b) => String(a.ts).localeCompare(String(b.ts)));
  const latest = inbound[inbound.length - 1];
  if (!latest) return null;
  const payload = latest.payload || {};
  const displayText = String(payload.excerpt || payload.summary || payload.body || latest.label || "");
  return { event: latest, displayText };
}
